import React from "react";
import { Link } from "react-router-dom";
import { altoSaxophoneSyllabus } from "../mock/altoSaxophoneSyllabus";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const levels = [1, 2, 3, 4, 5, 6];

const levelDescriptors = {
  1: "Foundation stage focusing on embouchure basics, breath control and fundamental musical literacy.",
  2: "Developing stage with improved tone, controlled articulation and expanded musical understanding.",
  3: "Musical expansion with secure tone across registers, expressive phrasing and rhythmic vocabulary.",
  4: "Artistic control with advanced articulation, stylistic awareness and expressive dynamics.",
  5: "Professional level demonstrating technical command, stylistic versatility and improvisational fluency.",
  6: "Mastery level certifying artistic authority, pedagogical understanding and creative voice.",
};

export default function AltoSaxophoneSyllabusOverview() {
  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <h1 className="text-2xl font-semibold tracking-tight text-[#182033]">
          Alto Saxophone Certification — Levels 1–6
        </h1>
        <p className="max-w-3xl text-sm leading-relaxed text-[#4b4a46] sm:text-base">
          The Global Music Conservatory Alto Saxophone syllabus provides a structured pathway
          from foundational embouchure and breath control through to professional-level
          performance, improvisation and pedagogical mastery. Each level includes practical
          and theory components designed to develop well-rounded saxophonists.
        </p>
      </section>

      <section className="space-y-4">
        <div className="overflow-x-auto rounded-2xl border border-[#e1d2b3] bg-[#fdf8ef]">
          <Table>
            <TableHeader className="bg-[#f5ebd9]">
              <TableRow>
                <TableHead className="w-[100px] text-xs font-semibold uppercase tracking-[0.16em] text-[#1f2738]">
                  Level
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-[0.16em] text-[#1f2738]">
                  Title
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-[0.16em] text-[#1f2738]">
                  Descriptor
                </TableHead>
                <TableHead className="w-[160px] text-right text-xs font-semibold uppercase tracking-[0.16em] text-[#1f2738]">
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {levels.map((levelNumber) => {
                const levelData = altoSaxophoneSyllabus[levelNumber];
                return (
                  <TableRow key={levelNumber} className="text-sm text-[#4b4a46]">
                    <TableCell className="align-top font-semibold text-[#182033]">
                      Level {levelNumber}
                    </TableCell>
                    <TableCell className="align-top font-medium text-[#182033]">
                      {levelData?.title?.split("—")[1]?.trim() || `Level ${levelNumber}`}
                    </TableCell>
                    <TableCell className="align-top text-[#4b4a46]">
                      {levelDescriptors[levelNumber]}
                    </TableCell>
                    <TableCell className="align-top text-right">
                      <Link
                        to={`/alto-saxophone-syllabus/level/${levelNumber}`}
                        className="inline-flex h-8 items-center justify-center rounded-full border border-[#d5c39d] bg-white px-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#1f2738] hover:bg-[#f5ebd9]"
                      >
                        View syllabus
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="rounded-2xl border border-[#ddcaa4] bg-[#f9f2e6] p-4 lg:p-6">
        <h2 className="mb-3 text-base font-semibold tracking-tight text-[#182033]">
          About the Alto Saxophone Certification
        </h2>
        <div className="space-y-2 text-sm leading-relaxed text-[#4b4a46]">
          <p>
            The Alto Saxophone certification programme combines technical development with
            musical expression, covering embouchure, breath support, articulation, scales,
            and stylistic interpretation across classical, jazz, and contemporary genres.
          </p>
          <p>
            Students progress through practical examinations featuring repertoire performance,
            scales, sight-reading, and improvisation, alongside theory assessments covering
            notation, harmony, and musical analysis.
          </p>
        </div>
      </section>
    </div>
  );
}
